// ===================================================================
// Shared utility: Print a sheet of property QR labels
// Generates a printable page of adhesive-style labels with QR codes
// that survivors can stick on their belongings
// ===================================================================

import QRCode from 'qrcode';

interface LabelItem {
  description: string;
  qrCode: string;
  storageLocation?: string;
}

/**
 * Print a sheet of property labels for a survivor.
 * Each label has a QR code, item description, owner name, and storage location.
 * Sized for standard 2"x2" label sheets (6 per page, 2 cols x 3 rows).
 * Also prints blank labels with the survivor's QR code for items not yet checked in.
 */
export async function printPropertyLabelSheet(
  ownerName: string,
  ownerQrCode: string,
  items: LabelItem[],
  blankCount = 0,
): Promise<void> {
  // Generate all QR code images
  const itemImages = await Promise.all(
    items.map(async (item) => ({
      ...item,
      qrImg: item.qrCode ? await QRCode.toDataURL(item.qrCode, { width: 200, margin: 1 }) : '',
    })),
  );

  // Generate owner QR for blank labels
  const ownerQrImg = ownerQrCode
    ? await QRCode.toDataURL(ownerQrCode, { width: 200, margin: 1 })
    : '';

  // Build blank labels
  const blanks = Array.from({ length: blankCount }, () => ({
    description: '(Unlabeled Item)',
    qrCode: ownerQrCode,
    storageLocation: '',
    qrImg: ownerQrImg,
  }));

  const allLabels = [...itemImages, ...blanks];

  const labelHtml = allLabels.map((item) => `
    <div class="label">
      <div class="label-header">OpenGov EMC</div>
      <div class="label-qr">${item.qrImg ? `<img src="${item.qrImg}" />` : ''}</div>
      <div class="label-code">${item.qrCode || ''}</div>
      <div class="label-desc">${item.description || 'Item'}</div>
      <div class="label-owner">${ownerName}</div>
      ${item.storageLocation ? `<div class="label-storage">${item.storageLocation}</div>` : ''}
    </div>
  `).join('');

  const w = window.open('', '_blank', 'width=700,height=900');
  if (!w) return;

  w.document.write(`<!DOCTYPE html><html><head><title>Property Labels — ${ownerName}</title><style>
    @page { size: letter; margin: 0.5in; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; }
    .sheet { display: flex; flex-wrap: wrap; gap: 0; justify-content: flex-start; }
    .label {
      width: 2.5in; height: 2.5in;
      border: 1px dashed #ccc;
      padding: 6px;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      page-break-inside: avoid;
      overflow: hidden;
    }
    .label-header { font-size: 7px; font-weight: 700; color: var(--color-primary-800); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
    .label-qr { margin: 2px 0; }
    .label-qr img { width: 100px; height: 100px; }
    .label-code { font-family: monospace; font-size: 7px; color: #888; margin-bottom: 2px; }
    .label-desc { font-size: 10px; font-weight: 700; text-align: center; line-height: 1.2; max-height: 2.4em; overflow: hidden; }
    .label-owner { font-size: 8px; color: #555; }
    .label-storage { font-size: 7px; color: #888; }
    .page-title { font-size: 11px; font-weight: 700; margin-bottom: 8px; color: #333; }
    .instructions { font-size: 9px; color: #666; margin-bottom: 12px; }
    @media print {
      body { -webkit-print-color-adjust: exact; }
      .label { border: 1px dashed #ddd; }
      .no-print { display: none; }
    }
  </style></head><body>
    <div class="no-print" style="padding: 12px; background: #f5f5f5; margin-bottom: 12px; text-align: center;">
      <strong>Property Labels for ${ownerName}</strong> &mdash; ${allLabels.length} label(s)
      <br/><span style="font-size: 12px; color: #666;">Cut along dashed lines and attach to belongings</span>
    </div>
    <div class="sheet">${labelHtml}</div>
  </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}
